/**
 * WorkspaceSwitcherSheet — bottom sheet that lists the user's workspaces
 * and lets them switch on tap. Matches the Slack / Instagram "switch
 * account" UX pattern.
 *
 * Shows logo + name + role chip (owner or shared-by). Tap an item to
 * switch, or hit "Manage workspaces" to jump to the full management
 * page.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Check, Plus, X } from "lucide-react";

import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { ROUTES } from "@/types";
import { initials } from "@/utils/format";
import { optimizedUrl, optimizedSrcSet } from "@/lib/files";

interface Props {
  onClose: () => void;
}

const WorkspaceSwitcherSheet: React.FC<Props> = ({ onClose }) => {
  const { workspaces, active, switchTo } = useWorkspace();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  // Trigger the CSS transition on mount.
  useEffect(() => {
    const t = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(t);
  }, []);

  // ESC to close + prevent body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const handleSwitch = (id: string) => {
    const w = workspaces.find((w) => w.id === id);
    if (w) { switchTo(w); setTimeout(onClose, 120); }
  };

  return (
    <>
      {/* Scrim */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 70,
          background: "rgba(0,0,0,0.42)",
          backdropFilter: "blur(4px)",
          opacity: mounted ? 1 : 0,
          transition: "opacity 240ms",
        }}
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Switch workspace"
        style={{
          position: "fixed",
          left: 0, right: 0, bottom: 0,
          zIndex: 71,
          background: "var(--bg-surface-raised)",
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: "8px 12px calc(env(safe-area-inset-bottom, 0px) + 16px)",
          boxShadow: "0 -20px 40px -12px rgba(0,0,0,0.25)",
          maxHeight: "min(72vh, 640px)",
          display: "flex",
          flexDirection: "column",
          transform: mounted ? "translateY(0)" : "translateY(100%)",
          transition: "transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        {/* Handle */}
        <div style={{
          width: 44, height: 4,
          background: "var(--border-strong, var(--border-default))",
          borderRadius: 999,
          margin: "8px auto 14px",
        }} />

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 8px 12px",
          borderBottom: "1px solid var(--border-default)",
        }}>
          <div>
            <div style={{
              fontSize: "var(--font-size-md)",
              fontWeight: 700,
              color: "var(--text-primary)",
              fontFamily: "var(--font-display)",
            }}>
              Your workspaces
            </div>
            <div style={{
              fontSize: "var(--font-size-xs)",
              color: "var(--text-tertiary)",
              marginTop: 2,
            }}>
              {workspaces.length} team{workspaces.length === 1 ? "" : "s"}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36, height: 36,
              borderRadius: "50%",
              background: "var(--bg-surface-alt)",
              border: "1px solid var(--border-default)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-secondary)",
              cursor: "pointer",
            }}
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>

        {/* List */}
        <div style={{
          flex: 1,
          overflowY: "auto",
          padding: "8px 4px 4px",
        }}>
          {workspaces.map((w) => {
            const selected = w.id === active?.id;
            const shared = user && w.userId !== user.id;
            return (
              <button
                key={w.id}
                onClick={() => handleSwitch(w.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  width: "100%", padding: "12px 12px",
                  background: selected ? "color-mix(in srgb, var(--accent-300) 10%, transparent)" : "transparent",
                  border: "none", borderRadius: 14,
                  cursor: "pointer", textAlign: "left",
                  marginBottom: 4,
                }}
              >
                <span style={{
                  width: 44, height: 44,
                  borderRadius: 12,
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-default)",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", flexShrink: 0,
                }}>
                  {w.logo ? (
                    <img
                      src={optimizedUrl(w, w.logo, { w: 96, q: 82 })}
                      srcSet={optimizedSrcSet(w, w.logo, 44, { q: 82 })}
                      alt=""
                      decoding="async"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <span style={{
                      fontSize: 14, fontWeight: 700,
                      color: "var(--text-tertiary)",
                      fontFamily: "var(--font-display)",
                    }}>
                      {initials(w.name)}
                    </span>
                  )}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{
                    display: "flex", alignItems: "center", gap: 8,
                    fontSize: "var(--font-size-md)",
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}>
                    <span style={{
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {w.name}
                    </span>
                    {shared && (
                      <span style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 9, fontWeight: 700,
                        letterSpacing: "var(--tracking-label)",
                        padding: "2px 6px",
                        borderRadius: 999,
                        background: "var(--brand-gradient)",
                        color: "#fff",
                        textTransform: "uppercase",
                        flexShrink: 0,
                      }}>
                        Shared
                      </span>
                    )}
                  </span>
                  {w.category && (
                    <span style={{
                      display: "block",
                      fontSize: "var(--font-size-xs)",
                      color: "var(--text-tertiary)",
                      marginTop: 2,
                    }}>
                      {w.category}
                    </span>
                  )}
                </span>
                {selected && (
                  <span style={{
                    width: 28, height: 28,
                    borderRadius: "50%",
                    background: "var(--brand-gradient)",
                    display: "inline-flex",
                    alignItems: "center", justifyContent: "center",
                    color: "#fff",
                    flexShrink: 0,
                  }}>
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div style={{
          display: "flex", gap: 8,
          paddingTop: 10,
          borderTop: "1px solid var(--border-default)",
        }}>
          <button
            onClick={() => { onClose(); navigate(ROUTES.WORKSPACES); }}
            style={{
              flex: 1,
              height: 44,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: "var(--bg-surface-alt)",
              border: "1px solid var(--border-default)",
              borderRadius: 12,
              color: "var(--text-primary)",
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <Building2 size={14} strokeWidth={1.75} />
            Manage
          </button>
          <button
            onClick={() => { onClose(); navigate(`${ROUTES.WORKSPACES}?new=1`); }}
            style={{
              flex: 1,
              height: 44,
              display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: "var(--brand-gradient)",
              border: "none",
              borderRadius: 12,
              color: "#fff",
              fontSize: "var(--font-size-sm)",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 8px 18px -6px rgba(197,63,255,0.45)",
            }}
          >
            <Plus size={14} strokeWidth={2} />
            New workspace
          </button>
        </div>
      </div>
    </>
  );
};

export default WorkspaceSwitcherSheet;
